import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuthV2";
import {
  Search,
  LogIn,
  User as UserIcon,
  Settings,
  LogOut,
  ChevronDown,
  ShieldIcon,
  HelpCircle,
  Lock,
  Bug,
  MessageSquare,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { chromeSelectClass } from "@/lib/map-chrome";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import logoImage from "@/assets/logo.png";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import { getModuleRegistry } from "@/modules/registry";
import { ModuleLauncher } from "@/components/ModuleLauncher";
import { GlobalSearchPalette } from "@/components/GlobalSearchPalette";
import { NotificationsBell } from "@/components/notifications/NotificationsBell";
import { ReportBugDialog } from "@/components/feedback/ReportBugDialog";
import { useScreenLock } from "@/contexts/ScreenLockContext";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface HeaderProps {
  className?: string;
  /** @deprecated Локальный фильтр модулей — через EntityListPanel; поиск глобальный. */
  onSearch?: (query: string) => void;
  /** FlyTo адреса на карте текущего workspace (ОИ и др.). */
  onPlaceSelect?: (place: { lon: number; lat: number; displayName: string }) => void;
}

export default function Header({ className, onSearch: _onSearch, onPlaceSelect }: HeaderProps) {
  const { user, isAuthenticated, logout, hasPermission } = useAuth();
  const { flags } = useModuleFlags();
  const moduleRegistry = getModuleRegistry(flags);
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { tryLock, screenLockEnabled } = useScreenLock();

  const handleLogout = () => {
    toast({
      title: "Выход из системы",
      description: "Выполняется выход из учетной записи...",
    });
    logout();
  };

  const handleLogin = () => {
    navigate("/login");
  };

  const handleScreenLockClick = () => {
    const reason = tryLock();
    if (reason === null) return;
    if (reason === "module_disabled") {
      toast({
        title: "Блокировка экрана недоступна",
        description: "Модуль выключен администратором системы.",
      });
      return;
    }
    toast({
      title: "Сначала установите PIN",
      description: "Откройте Настройки → Безопасность и задайте PIN для блокировки экрана.",
    });
    navigate("/settings");
  };

  useEffect(() => {
    if (!isAuthenticated) {
      setSearchOpen(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k") return;
      if (!(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      setSearchOpen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAuthenticated]);

  const visibleModules = moduleRegistry.filter((item) => {
    // Чат открывается из хедера (иконка), в launcher не дублируем.
    if (item.code === "chat") return false;
    if (!item.enabled) return false;
    if (!isAuthenticated) return false;
    if (!item.requiredPermission) return true;
    return hasPermission(item.requiredPermission);
  });
  const activeModule = moduleRegistry.find(
    (module) => location.pathname === module.route || location.pathname.startsWith(`${module.route}/`),
  );
  const chatModule = moduleRegistry.find((m) => m.code === "chat");
  const showChatShortcut =
    Boolean(chatModule?.enabled) &&
    isAuthenticated &&
    (!chatModule?.requiredPermission || hasPermission(chatModule.requiredPermission));
  const chatActive =
    location.pathname === "/chat" || location.pathname.startsWith("/chat/");

  return (
    <header
      data-onboarding="app-header"
      className={cn(
        "relative bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 shadow-sm border-b border-border",
        chromeSelectClass,
        className,
      )}
    >
      {isAuthenticated && (
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center leading-none">
          <div className="pointer-events-auto -mt-px">
            <ModuleLauncher modules={visibleModules} activeModule={activeModule} />
          </div>
        </div>
      )}

      <div className="grid w-full grid-cols-[1fr_auto] items-center gap-2 px-4 py-3 sm:gap-3">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <img src={logoImage} alt="GeoPulseML" className="h-8 w-8 shrink-0 rounded-lg" />
          <Link to="/oi" className="hidden truncate text-lg font-semibold text-foreground sm:inline">
            ГИС GeoPulseML
          </Link>
        </div>

        <div className="flex items-center justify-end gap-1 sm:gap-2">
          {isAuthenticated && (
            <>
              <div className="flex items-center">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-foreground hover:bg-muted"
                  aria-label="Глобальный поиск (Ctrl+K)"
                  title="Поиск по системе (Ctrl/⌘K)"
                  onClick={() => setSearchOpen(true)}
                >
                  <Search className="h-[1.125rem] w-[1.125rem]" strokeWidth={2.25} />
                </Button>
                <GlobalSearchPalette
                  open={searchOpen}
                  onOpenChange={setSearchOpen}
                  onPlaceSelect={onPlaceSelect}
                />
              </div>
              {showChatShortcut && (
                <>
                  <div
                    className="mx-1.5 h-5 w-px shrink-0 bg-border sm:mx-2"
                    aria-hidden
                  />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn(
                          "h-9 w-9",
                          chatActive
                            ? "bg-muted text-foreground"
                            : "text-foreground hover:bg-muted",
                        )}
                        aria-label="Чат"
                        aria-current={chatActive ? "page" : undefined}
                        onClick={() => navigate("/chat")}
                      >
                        <MessageSquare className="h-[1.125rem] w-[1.125rem]" strokeWidth={2.25} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left">
                      <p className="font-medium">{chatModule?.label ?? "Чат"}</p>
                      <p className="text-xs opacity-90">
                        {chatModule?.description ??
                          "Личные и групповые чаты со сквозным шифрованием"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
              <div
                className="mx-1.5 h-5 w-px shrink-0 bg-border sm:mx-2"
                aria-hidden
              />
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={
                    screenLockEnabled
                      ? "h-9 w-9 text-foreground hover:bg-muted"
                      : "h-9 w-9 text-muted-foreground hover:text-foreground"
                  }
                  aria-label="Заблокировать экран"
                  title={
                    screenLockEnabled
                      ? "Заблокировать экран (PIN)"
                      : "Блокировка экрана — настройте PIN в Безопасности"
                  }
                  onClick={handleScreenLockClick}
                >
                  <Lock className="h-4 w-4" />
                </Button>
                {flags.moduleFeedbackEnabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-muted-foreground hover:text-foreground"
                    aria-label="Обратная связь"
                    title="Сообщить о проблеме"
                    onClick={() => setFeedbackOpen(true)}
                  >
                    <Bug className="h-4 w-4" />
                  </Button>
                )}
                <NotificationsBell />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-muted-foreground hover:text-foreground"
                  aria-label="Справка"
                  title="Справка"
                  asChild
                >
                  <Link to="/help">
                    <HelpCircle className="h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </>
          )}

          {isAuthenticated ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.profile_image_url ? String(user.profile_image_url) : undefined} alt="Profile" />
                    <AvatarFallback>
                      {user?.first_name?.[0] || user?.email?.[0] || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden font-medium md:inline-block">
                    {user?.first_name || user?.email?.split("@")[0] || "Пользователь"}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <Link to="/profile">
                  <DropdownMenuItem>
                    <UserIcon className="mr-2 h-4 w-4" />
                    <span>Профиль</span>
                  </DropdownMenuItem>
                </Link>
                {hasPermission("admin.access") && (
                  <Link to="/admin">
                    <DropdownMenuItem>
                      <ShieldIcon className="mr-2 h-4 w-4" />
                      <span>Админ-панель</span>
                    </DropdownMenuItem>
                  </Link>
                )}
                <Link to="/settings">
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Настройки</span>
                  </DropdownMenuItem>
                </Link>
                <Link to="/help">
                  <DropdownMenuItem>
                    <HelpCircle className="mr-2 h-4 w-4" />
                    <span>Помощь</span>
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>Выйти</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" className="flex items-center space-x-1" onClick={handleLogin}>
              <LogIn className="h-4 w-4" />
              <span>Войти</span>
            </Button>
          )}
        </div>
      </div>

      {flags.moduleFeedbackEnabled && (
        <ReportBugDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}
    </header>
  );
}
